import { useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import StepHeader from 'components/StepHeader';
import Toaster from 'components/Toaster';
import { useCreateTransactionCredit } from 'components/Transactions/gql/useCreateTransactionCredit';
import { useGetRefundEstimate } from 'components/Transactions/gql/useGetRefundEstimate';
import { useRefundPasses } from 'components/Transactions/gql/useRefundPasses';
import { AppContext } from 'shared/AppContext';
import { formatPhoneNumber } from 'shared/formatters';
import GetCustomer from '../../gql/get-customer';
import GetUserOpenCreditsTotal from '../../gql/get-user-open-credits-total';
import GetTransactionDetail from '../../gql/queries/get-transaction-detail';
import Step2MessageComposer from '../IssueCredit/Step2MessageComposer';
import {
  PageContainer,
  PageContent,
  PageTitle,
  spin,
} from '../IssueCredit/StyledComponents';
import {
  FooterButtons,
  NextButton,
  SendButton,
} from '../SendCampaign/StyledComponents';
import Step1SelectCreditMethod from './Step1SelectCreditMethod';
import Step3Review from './Step3Review';

const SpinnerIcon = styled.svg`
  animation: ${spin} 1s linear infinite;
  margin-right: 8px;
`;

// Helper function to get ticket/pass text based on platform
const getTicketText = (count = 1) => {
  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  if (isTicketHoss) {
    return count === 1 ? 'ticket' : 'tickets';
  }
  return count === 1 ? 'pass' : 'passes';
};

// Helper function to get total available (non-refunded) ticket count from transaction
const getTotalAvailableTicketCount = (transactionInfo) => {
  if (!transactionInfo) return 0;

  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  let totalCount = 0;

  if (isTicketHoss) {
    // For tickethoss, tickets are in a flat array
    totalCount =
      transactionInfo.tickets?.filter((t) => !t.refunded && !t.credited)
        .length || 0;
  } else {
    // For pitpay, tickets are grouped by user
    if (transactionInfo.users) {
      transactionInfo.users.forEach((userGroup) => {
        const availableTickets =
          userGroup.tickets?.filter((t) => !t.refunded && !t.credited).length ||
          0;
        totalCount += availableTickets;
      });
    }
  }

  return totalCount;
};

// Helper function to determine refund scenario type
// Returns: 'normal_refund', 'full_credit', 'mixed', or 'zero_dollar_refund'
const getRefundScenario = (
  creditRestored,
  refundAmount,
  transactionTotal = null
) => {
  const credit = parseFloat(creditRestored) || 0;
  const refund = parseFloat(refundAmount) || 0;
  const total = transactionTotal !== null ? parseFloat(transactionTotal) : null;

  if (credit > 0 && refund === 0) {
    return 'full_credit'; // Only credit, no refund
  } else if (credit > 0 && refund > 0) {
    return 'mixed'; // Both credit and refund
  } else if (refund > 0) {
    return 'normal_refund'; // Normal refund with money back
  } else if (refund === 0) {
    // When refund is 0, check if this is truly a zero-dollar transaction
    // If we know the transaction total was > 0, this is likely an error or loading state
    // Only return 'zero_dollar_refund' if we can't determine the total (null) or if total is 0
    if (total !== null && total > 0) {
      // Transaction had value but refund is 0 - default to normal_refund to avoid wrong message
      return 'normal_refund';
    }
    return 'zero_dollar_refund'; // No money refund (100% discount purchase)
  } else {
    return 'normal_refund'; // Default fallback
  }
};

// Helper function to get default message template based on scenario
const getDefaultMessage = (scenario, type, isAllTickets = false) => {
  // If type is 'credit', always use credit message
  if (type === 'credit') {
    return `Hi {{first_name}}! You have a {{credit_amount}} credit from {{track_name}}. To view: Open app > Menu > Payments > Select credit at top.`;
  }

  // For refunds, determine message based on scenario
  switch (scenario) {
    case 'full_credit':
      // Customer paid with credit, getting credit back - use credit message
      return `Hi {{first_name}}! You have a {{credit_amount}} credit from {{track_name}}. To view: Open app > Menu > Payments > Select credit at top.`;

    case 'mixed':
      // Customer getting both credit and refund
      return `Hi {{first_name}}, {{track_name}} issued you a {{credit_amount}} credit and a {{refund_amount}} refund to your original payment method. Allow 3-5 business days for the refund to process.`;

    case 'zero_dollar_refund':
      // Customer paid with 100% discount/promo, refunding tickets without money
      if (isAllTickets) {
        return `Hi {{first_name}}, {{track_name}} has refunded your tickets. Since you used a discount code, no payment will be issued.`;
      } else {
        return `Hi {{first_name}}, {{track_name}} has refunded some of your tickets. Since you used a discount code, no payment will be issued.`;
      }

    case 'normal_refund':
    default:
      // Normal refund only
      return `Hi {{first_name}}, {{track_name}} issued you a {{refund_amount}} refund to your original payment method. Allow 3-5 business days to process.`;
  }
};

// Helper function to get text based on adjustment type and scenario
const getAdjustmentText = (type, key, scenario = 'normal_refund') => {
  const texts = {
    credit: {
      title: 'Issue Credit',
      pageTitle: 'Issue Credit',
      amount: 'Credit Amount',
      button: 'Issue Credit',
      sendButton: 'Send Credit',
      success: 'Credit Issued Successfully',
      successMessage: 'Credit has been issued',
      messageTitle: 'You received a credit!',
      step1Title: 'Select Credit Method',
      step3Title: 'Review Credit',
    },
    refund: {
      title: 'Issue Refund',
      pageTitle: 'Issue Refund',
      amount: 'Refund Amount',
      button: 'Issue Refund',
      sendButton: 'Send Refund',
      success: 'Refund Issued Successfully',
      successMessage: 'Refund has been issued',
      messageTitle: 'You received a refund!',
      step1Title: 'Select Refund Method',
      step3Title: 'Review Refund',
    },
    full_credit: {
      // When refund is actually a credit restoration - but still a refund action
      title: 'Issue Refund',
      pageTitle: 'Issue Refund',
      amount: 'Credit Amount',
      button: 'Issue Refund',
      sendButton: 'Send Refund',
      success: 'Refund Issued Successfully',
      successMessage: 'Refund has been issued',
      messageTitle: 'You received a credit!',
      step1Title: 'Select Refund Method',
      step3Title: 'Review Refund',
    },
    mixed: {
      // When refund is both credit and refund - but still a refund action
      title: 'Issue Refund',
      pageTitle: 'Issue Refund',
      amount: 'Total Amount',
      button: 'Issue Refund',
      sendButton: 'Send Refund',
      success: 'Refund Issued Successfully',
      successMessage: 'Refund has been issued',
      messageTitle: 'You received a credit and refund!',
      step1Title: 'Select Refund Method',
      step3Title: 'Review Refund',
    },
  };

  // For refund type, use scenario to determine which text to use
  if (type === 'refund' && scenario !== 'normal_refund') {
    return texts[scenario]?.[key] || texts.refund[key];
  }

  return texts[type]?.[key] || texts.credit[key];
};

const TransactionCredit = ({ onSuccess, type = 'credit' }) => {
  const history = useHistory();
  const location = useLocation();
  const { transactionId } = useParams();
  const { state } = useContext(AppContext);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [toasterData, setToasterData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Get customerId from URL params (may be null for guest purchases)
  const searchParams = new URLSearchParams(location.search);
  const customerId = searchParams.get('customerId');

  // Determine route prefix based on current path
  const isTrackAdmin = window.location.pathname.includes('/admin-track');
  const isEmployeeAdmin = window.location.pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';
  const defaultReturnPath = customerId
    ? `${routePrefix}/users/transactions/${customerId}`
    : `${routePrefix}/transactions/detail/${transactionId}`;
  const returnTo = searchParams.get('returnTo') || defaultReturnPath;
  const customersPath = returnTo;

  // Events URL removed - no longer needed in messages

  // Get account ID - used for some queries
  let accountId = null;

  // Get accountId from context or localStorage
  if (state && state.account) {
    accountId = state.account.id;
  } else {
    // Fallback to localStorage if context doesn't have it
    try {
      const accountData = localStorage.getItem('selected_account');
      if (accountData) {
        const account = JSON.parse(accountData);
        accountId = account.id;
      }
    } catch (error) {
      // Error reading account from localStorage
    }
  }

  // Credit data state
  const [creditData, setCreditData] = useState({
    transactionId: transactionId,
    customerId: customerId,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    currentBalance: 0,

    // Transaction specific data
    transactionInfo: null,
    creditMethod: 'tickets', // 'tickets' or 'custom'
    selectedTickets: [],
    includeProcessingFees: true,
    customAmount: '',
    dynamicProcessingFees: 0, // Dynamic fees from backend
    creditRestored: 0, // Credit amount restored from backend

    // Original credit fields
    amount: '0.00',
    expiration: null,
    reason: '',
    sendMessage: true,
    messageChannel: 'SMS',
    messageContent: getDefaultMessage('normal_refund', type), // Use helper function for default
    messageTitle: getAdjustmentText(type, 'messageTitle'),
    scheduledDateTime: null,
  });

  // Fetch transaction details using GraphQL
  const {
    data: transactionData,
    loading: transactionLoading,
    error: transactionError,
  } = useQuery(GetTransactionDetail, {
    variables: {
      purchase_id: parseInt(transactionId),
    },
    skip: !transactionId,
    fetchPolicy: 'no-cache',
  });

  // Update transaction info when data is loaded
  useEffect(() => {
    if (transactionData && transactionData.getTrackTransaction) {
      const transaction = transactionData.getTrackTransaction;

      // Helper function to parse money strings like "$7.80"
      const parseMoneyString = (value) => {
        if (!value) return 0;
        if (typeof value === 'number') return value;
        // Remove dollar sign and commas, then parse
        const cleaned = value.toString().replace(/[$,]/g, '');
        return parseFloat(cleaned) || 0;
      };

      // Extract tickets - for tickethoss, flatten all tickets; for others, group by user
      const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
      const formattedTickets = [];
      const formattedUsers = [];

      if (transaction.users) {
        transaction.users.forEach((userGroup) => {
          if (userGroup.tickets) {
            const userTickets = [];
            userGroup.tickets.forEach((ticket) => {
              const formattedTicket = {
                id: ticket.id,
                name:
                  ticket.name ||
                  getTicketText(1).charAt(0).toUpperCase() +
                    getTicketText(1).slice(1),
                serialNumber: ticket.barcode || '',
                price: parseMoneyString(ticket.price),
                type: ticket.type,
                section: ticket.section,
                row: ticket.row,
                seat: ticket.seat,
                refunded: ticket.refunded || false,
                credited: ticket.credited || false,
              };

              // For tickethoss, add to flat list
              if (isTicketHoss) {
                formattedTickets.push(formattedTicket);
              } else {
                // For others, group by user
                userTickets.push(formattedTicket);
              }
            });

            // For non-tickethoss, create user groups
            if (!isTicketHoss && userTickets.length > 0) {
              formattedUsers.push({
                user: userGroup.user,
                tickets: userTickets,
              });
            }
          }
        });
      }

      // Also extract user info from transaction for when GetCustomer doesn't work (regular admin)
      let transactionUserName = '';
      let transactionUserEmail = '';
      let transactionUserPhone = '';

      if (transaction.user) {
        transactionUserName =
          `${transaction.user.first_name || ''} ${
            transaction.user.last_name || ''
          }`.trim() ||
          transaction.user.name ||
          transaction.user.formatted_name ||
          '';
        transactionUserEmail = transaction.user.email || '';
        transactionUserPhone = transaction.user.cellphone || '';

        // Format phone number if present
        if (transactionUserPhone) {
          transactionUserPhone =
            formatPhoneNumber(transactionUserPhone) || transactionUserPhone;
          // Remove the +1 prefix if present
          if (transactionUserPhone.startsWith('+1 ')) {
            transactionUserPhone = transactionUserPhone.substring(3);
          }
        }
      }

      setCreditData((prev) => ({
        ...prev,
        transactionInfo: {
          id: transaction.id,
          eventName: transaction.event?.name || 'Event',
          eventDate: transaction.event?.start_date || transaction.purchase_date,
          purchaseDate: transaction.purchase_date,
          total: parseMoneyString(transaction.original_total),
          processingFees: parseMoneyString(transaction.fee),
          creditUsed: parseMoneyString(transaction.credit), // Store credit used at checkout
          tickets: isTicketHoss ? formattedTickets : [],
          users: isTicketHoss ? [] : formattedUsers,
          show_fee: transaction.show_fee !== false, // Keep as snake_case
          transactionUserId: transaction.user?.id || null, // Store user ID from transaction
          transactionUserName,
          transactionUserEmail,
          transactionUserPhone,
          // Store account info from transaction for main admin context
          accountBusinessDba: transaction.account?.business_dba || null,
        },
        // Reset selections when transaction changes
        selectedTickets: [],
        amount: '0.00',
        // Set includeProcessingFees based on show_fee field
        includeProcessingFees: transaction.show_fee !== false,
      }));
    }
  }, [transactionData]);

  // Determine effective user ID (from URL or transaction)
  const effectiveUserId =
    customerId || creditData.transactionInfo?.transactionUserId;

  // Fetch customer data using GraphQL
  const {
    data: customerData,
    loading: customerLoading,
    error: customerError,
  } = useQuery(GetCustomer, {
    variables: {
      input: {
        user_id: parseInt(effectiveUserId),
        account_id: accountId ? parseInt(accountId) : null,
      },
    },
    skip: !effectiveUserId || !accountId,
  });

  // Fetch current credit balance using GraphQL
  const {
    data: balanceData,
    loading: balanceLoading,
    error: balanceError,
  } = useQuery(GetUserOpenCreditsTotal, {
    variables: {
      input: {
        user_id: parseInt(effectiveUserId),
        account_id: accountId ? parseInt(accountId) : null,
      },
    },
    skip: !effectiveUserId || !accountId,
  });

  // Update credit data when customer data is fetched
  useEffect(() => {
    if (customerData && customerData.getCustomer) {
      const customer = customerData.getCustomer;
      // Format phone number without the +1 prefix
      let formattedPhone =
        formatPhoneNumber(customer.cellphone) || customer.cellphone || '';
      // Remove the +1 prefix if present to match desired format
      if (formattedPhone.startsWith('+1 ')) {
        formattedPhone = formattedPhone.substring(3);
      }

      setCreditData((prev) => ({
        ...prev,
        customerName: `${customer.first_name} ${customer.last_name}`.trim(),
        customerEmail: customer.email || '',
        customerPhone: formattedPhone,
      }));
    } else if (
      !customerLoading &&
      effectiveUserId &&
      creditData.transactionInfo
    ) {
      // GetCustomer didn't return data (e.g., regular admin context)
      // Use transaction user data as fallback
      setCreditData((prev) => ({
        ...prev,
        customerName: prev.transactionInfo?.transactionUserName || '',
        customerEmail: prev.transactionInfo?.transactionUserEmail || '',
        customerPhone: prev.transactionInfo?.transactionUserPhone || '',
      }));
    } else if (!customerLoading && !effectiveUserId) {
      // Handle true guest customer - no user ID at all
      setCreditData((prev) => ({
        ...prev,
        customerName: 'Guest Customer',
        customerEmail: '',
        customerPhone: '',
        sendMessage: false, // Disable messaging for true guest customers
      }));
    }
  }, [
    customerData,
    customerLoading,
    effectiveUserId,
    creditData.transactionInfo,
  ]);

  // Update credit balance when fetched
  useEffect(() => {
    if (
      balanceData &&
      typeof balanceData.getUserOpenCreditsTotal !== 'undefined'
    ) {
      setCreditData((prev) => ({
        ...prev,
        currentBalance: parseFloat(balanceData.getUserOpenCreditsTotal) || 0,
      }));
    }
  }, [balanceData]);

  // Compute track name with priority:
  // 1. Transaction account data (works for all admin contexts including main admin)
  // 2. AppContext (works for track/employee admin)
  // 3. localStorage (fallback for track/employee admin)
  // 4. Default fallback

  // Extract specific values to watch (avoids ESLint warnings about complex expressions)
  const accountBusinessDba = creditData.transactionInfo?.accountBusinessDba;
  const accountDba = state?.account?.business_dba;
  const accountName = state?.account?.business_name;

  const trackName = useMemo(() => {
    // First priority: transaction account data (available for all contexts)
    if (accountBusinessDba) {
      return accountBusinessDba;
    }

    // Second priority: AppContext (for track/employee admin)
    if (accountDba) {
      return accountDba;
    }
    if (accountName) {
      return accountName;
    }

    // Third priority: localStorage (for track/employee admin)
    try {
      const accountData = localStorage.getItem('account');
      if (accountData) {
        const account = JSON.parse(accountData);
        if (account.business_dba) {
          return account.business_dba;
        }
        if (account.business_name) {
          return account.business_name;
        }
      }
    } catch (e) {
      // Error parsing localStorage
    }

    // Default fallback
    return 'Your Track';
  }, [accountBusinessDba, accountDba, accountName]);

  // Get selected ticket IDs for refund estimate
  const selectedTicketIds = creditData.selectedTickets.map(
    (ticket) => ticket.id
  );

  // Fetch refund estimate for dynamic fees (only for ticket method)
  const { data: refundEstimateData, loading: refundEstimateLoading } =
    useGetRefundEstimate(
      parseInt(transactionId),
      creditData.creditMethod === 'tickets' ? selectedTicketIds : [], // Only pass IDs for ticket method
      creditData.includeProcessingFees // Pass the actual checkbox state
    );

  // Update dynamic processing fees and refund amount when refund estimate changes
  useEffect(() => {
    if (
      refundEstimateData?.refundEstimate &&
      refundEstimateData.refundEstimate.refund_fees !== undefined
    ) {
      // Remove dollar sign and parse as float
      const feesString = refundEstimateData.refundEstimate.refund_fees;
      const feesValue = parseFloat(feesString.replace(/[$,]/g, '')) || 0;

      const amountString = refundEstimateData.refundEstimate.refund_amount;
      const amountValue = parseFloat(amountString.replace(/[$,]/g, '')) || 0;

      const creditRestoredString =
        refundEstimateData.refundEstimate.credit_restored;
      const creditRestoredValue =
        parseFloat(creditRestoredString?.replace(/[$,]/g, '') || '0') || 0;

      setCreditData((prev) => ({
        ...prev,
        dynamicProcessingFees: feesValue,
        dynamicRefundAmount: amountValue,
        creditRestored: creditRestoredValue,
      }));
    } else if (selectedTicketIds.length === 0) {
      // Reset fees and amount when no tickets selected
      setCreditData((prev) => ({
        ...prev,
        dynamicProcessingFees: 0,
        dynamicRefundAmount: 0,
        creditRestored: 0,
      }));
    }
  }, [refundEstimateData, selectedTicketIds.length]);

  // Initialize mutation hooks
  const refundPassesMutation = useRefundPasses();
  const createTransactionCreditMutation = useCreateTransactionCredit();

  // Update message content when refund scenario changes
  useEffect(() => {
    if (type === 'refund') {
      const scenario = getRefundScenario(
        creditData.creditRestored,
        creditData.dynamicRefundAmount,
        creditData.transactionInfo?.total
      );

      // Determine if refunding all tickets
      const totalAvailableTickets = getTotalAvailableTicketCount(
        creditData.transactionInfo
      );
      const selectedTicketCount = creditData.selectedTickets.length;
      const isAllTickets =
        selectedTicketCount > 0 &&
        selectedTicketCount === totalAvailableTickets;

      const newMessage = getDefaultMessage(scenario, type, isAllTickets);

      // Only update if the message hasn't been manually changed
      // Check if current message matches one of the default templates (check both variants)
      const isDefaultMessage =
        creditData.messageContent ===
          getDefaultMessage('normal_refund', type, false) ||
        creditData.messageContent ===
          getDefaultMessage('full_credit', type, false) ||
        creditData.messageContent === getDefaultMessage('mixed', type, false) ||
        creditData.messageContent ===
          getDefaultMessage('zero_dollar_refund', type, false) ||
        creditData.messageContent ===
          getDefaultMessage('zero_dollar_refund', type, true);

      if (isDefaultMessage || !creditData.messageContent) {
        setCreditData((prev) => ({
          ...prev,
          messageContent: newMessage,
        }));
      }
    }
  }, [
    creditData.creditRestored,
    creditData.dynamicRefundAmount,
    creditData.selectedTickets.length,
    creditData.transactionInfo,
    type,
    creditData.messageContent,
  ]);

  useEffect(() => {
    if (creditData.creditMethod === 'tickets') {
      // Use dynamic refund amount from backend
      if (creditData.dynamicRefundAmount) {
        setCreditData((prev) => ({
          ...prev,
          amount: creditData.dynamicRefundAmount.toFixed(2),
        }));
      }
    } else if (creditData.creditMethod === 'custom') {
      setCreditData((prev) => ({
        ...prev,
        amount: prev.customAmount,
      }));
    }
  }, [
    creditData.creditMethod,
    creditData.selectedTickets,
    creditData.includeProcessingFees,
    creditData.customAmount,
    creditData.dynamicProcessingFees,
    creditData.dynamicRefundAmount,
  ]);

  // Template variable replacement removed - now handled by backend

  const handleBack = () => {
    if (currentStep === 1) {
      history.replace(customersPath);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      // Validate based on credit method
      if (creditData.creditMethod === 'tickets') {
        if (creditData.selectedTickets.length === 0) {
          setToasterData({
            title: 'Error',
            message: `Please select at least one ${getTicketText(1)} to ${
              type === 'refund' ? 'refund' : 'credit'
            }.`,
            type: 'error',
          });
          return;
        }
      } else if (creditData.creditMethod === 'custom') {
        const parsedAmount = parseFloat(creditData.customAmount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
          setToasterData({
            title: 'Error',
            message: 'Please enter a valid credit amount greater than $0.00',
            type: 'error',
          });
          return;
        }
      }
      // Skip step 2 if notifications are disabled
      if (!creditData.sendMessage) {
        setCurrentStep(2); // Go to Review (which is step 2 when message is skipped)
      } else {
        setCurrentStep(2); // Go to Message step
      }
    } else if (currentStep === 2) {
      // Check if we're on Message step or Review step
      if (creditData.sendMessage && steps.length === 3) {
        // We're on Message step, validate and go to Review
        if (
          !creditData.messageContent ||
          creditData.messageContent.trim() === ''
        ) {
          setToasterData({
            title: 'Error',
            message:
              'Message content is required when notifications are enabled',
            type: 'error',
          });
          return;
        }
        setCurrentStep(3); // Go to Review
      }
      // If we're already on Review (when message is skipped), do nothing
    }
  };

  const handleIssueCredit = async () => {
    // Prevent multiple clicks
    if (isProcessing) return;

    try {
      setIsProcessing(true);

      // Show loading state
      setToasterData({
        title: 'Processing',
        message: `Issuing ${type}...`,
        type: 'info',
      });

      // Extract pass_ids from selected tickets
      const passIds = creditData.selectedTickets.map((ticket) => ticket.id);
      const creditAmount = parseFloat(creditData.amount);
      // Only include fees if show_fee is true AND the checkbox is checked
      const includeFeesInRefund =
        creditData.transactionInfo?.show_fee !== false
          ? creditData.includeProcessingFees
          : false;

      // Get track name for template context - use the same computed trackName from component scope
      // (It already prioritizes transaction account data, then AppContext, then localStorage)

      // Prepare template context for static values when sending message
      const templateContext = creditData.sendMessage
        ? {
            credit_amount: creditData.creditRestored
              ? `$${parseFloat(creditData.creditRestored).toFixed(2)}`
              : `$${creditAmount.toFixed(2)}`,
            refund_amount: creditData.dynamicRefundAmount
              ? `$${parseFloat(creditData.dynamicRefundAmount).toFixed(2)}`
              : `$${creditAmount.toFixed(2)}`,
            track_name: trackName, // Use the computed trackName from component scope
          }
        : null;

      let response;

      if (type === 'refund') {
        // For refunds, pass the raw message template if messaging is enabled
        const messageToSend = creditData.sendMessage
          ? creditData.messageContent // Send raw template, not replaced
          : '';

        // Pass either pass_ids OR credit_amount based on method
        if (creditData.creditMethod === 'tickets') {
          // Ticket-based refund: pass only pass_ids
          response = await refundPassesMutation(
            passIds,
            null, // event_id not needed for transaction-based refunds
            parseInt(transactionId),
            includeFeesInRefund,
            null, // NO credit_amount for ticket refunds
            messageToSend,
            templateContext
          );
        } else {
          // Custom amount refund: pass only credit_amount
          response = await refundPassesMutation(
            [], // NO pass_ids for custom amount refunds
            null, // event_id not needed for transaction-based refunds
            parseInt(transactionId),
            includeFeesInRefund,
            creditAmount,
            messageToSend,
            templateContext
          );
        }
      } else {
        // For credits, use the credit mutation with raw message template
        const messageToSend = creditData.sendMessage
          ? creditData.messageContent // Send raw template, not replaced
          : '';

        // Pass either pass_ids OR credit_amount based on method
        if (creditData.creditMethod === 'tickets') {
          // Ticket-based credit: pass only pass_ids
          response = await createTransactionCreditMutation(
            parseInt(transactionId),
            passIds,
            null, // NO credit_amount for ticket credits
            includeFeesInRefund,
            messageToSend,
            templateContext
          );
        } else {
          // Custom amount credit: pass only credit_amount
          response = await createTransactionCreditMutation(
            parseInt(transactionId),
            [], // NO pass_ids for custom amount credits
            creditAmount,
            includeFeesInRefund,
            messageToSend,
            templateContext
          );
        }
      }

      // Check for errors in response
      if (!response) {
        throw new Error(`Failed to issue ${type}`);
      }

      // Check for GraphQL errors - these come back in the response, not as thrown errors
      // The errors can be at response.errors or response.data.errors
      const errors = response.errors || response.data?.errors;
      if (errors && errors.length > 0) {
        const errorMessage = errors[0].message || `Failed to issue ${type}`;
        console.error('GraphQL error:', errors);
        throw new Error(errorMessage);
      }

      // Check for successful mutation result
      const mutationResult =
        type === 'refund'
          ? response.data?.createRefundV3
          : response.data?.createTransactionCredit;

      if (!mutationResult) {
        console.error('No mutation result in response:', response);
        throw new Error(
          `${type === 'refund' ? 'Refund' : 'Credit'} could not be issued`
        );
      }

      // Determine the scenario for success messages
      const scenario =
        type === 'refund'
          ? getRefundScenario(
              creditData.creditRestored,
              creditData.dynamicRefundAmount,
              creditData.transactionInfo?.total
            )
          : 'normal_refund';

      // Store success message in sessionStorage for the return page to display
      const adjustmentType = type === 'refund' ? 'refund' : 'credit';
      const customerDisplay =
        creditData.customerName === 'Anonymous Customer'
          ? 'customer'
          : creditData.customerName;

      // Check if this is a $0 refund (100% discount/promo purchase)
      const refundAmount = parseFloat(creditData.amount) || 0;
      const isZeroDollarRefund = type === 'refund' && refundAmount === 0;

      let successMessage;
      if (isZeroDollarRefund) {
        // For $0 refunds, use a generic message without mentioning money
        const ticketCount = creditData.selectedTickets.length;
        const ticketText = getTicketText(ticketCount);
        successMessage = creditData.sendMessage
          ? `Successfully processed refund for ${customerDisplay}. ${ticketCount} ${ticketText} refunded. Notification sent.`
          : `Successfully processed refund for ${customerDisplay}. ${ticketCount} ${ticketText} refunded.`;
      } else {
        // Normal success message with dollar amount
        successMessage = creditData.sendMessage
          ? `Successfully issued $${creditData.amount} ${adjustmentType} to ${customerDisplay}. Notification sent.`
          : `Successfully issued $${creditData.amount} ${adjustmentType} to ${customerDisplay}.`;
      }

      sessionStorage.setItem('creditSuccessMessage', successMessage);

      // Show success toast
      setToasterData({
        title: getAdjustmentText(type, 'success', scenario),
        message: getAdjustmentText(type, 'successMessage', scenario),
        type: 'success',
      });

      if (onSuccess) {
        onSuccess({
          customerName: creditData.customerName,
          amount: creditData.amount,
          messageIncluded: creditData.sendMessage,
        });
      }

      // Redirect immediately - the transaction detail page will show the toaster
      history.replace(customersPath);
    } catch (error) {
      console.error(`Error issuing ${type}:`, error);
      setIsProcessing(false);

      // Extract error message from different error formats
      let errorMessage = `Failed to issue ${type}. Please try again.`;

      // Handle Apollo errors which have graphQLErrors array
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage =
          'Network error. Please check your connection and try again.';
      } else if (error.message) {
        // For our custom thrown errors, extract the message
        // Remove "GraphQL error: " prefix if present
        errorMessage = error.message.replace(/^GraphQL error: /, '');
      }

      // Show error toast
      setToasterData({
        title: 'Error',
        message: errorMessage,
        type: 'error',
      });
    }
  };

  const getStepComponent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1SelectCreditMethod
            creditData={creditData}
            setCreditData={setCreditData}
            type={type}
            refundEstimateLoading={refundEstimateLoading}
          />
        );
      case 2:
        // If messaging is disabled, show Review. Otherwise show Message
        if (!creditData.sendMessage) {
          return (
            <Step3Review
              creditData={creditData}
              trackName={trackName}
              transactionData={creditData.transactionInfo}
              type={type}
            />
          );
        } else {
          return (
            <Step2MessageComposer
              creditData={creditData}
              setCreditData={setCreditData}
              trackName={trackName}
              type={type}
            />
          );
        }
      case 3:
        return (
          <Step3Review
            creditData={creditData}
            trackName={trackName}
            transactionData={creditData.transactionInfo}
            type={type}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    // Determine the current scenario for dynamic text
    const scenario =
      type === 'refund'
        ? getRefundScenario(
            creditData.creditRestored,
            creditData.dynamicRefundAmount,
            creditData.transactionInfo?.total
          )
        : 'normal_refund';

    switch (currentStep) {
      case 1:
        return getAdjustmentText(type, 'step1Title', scenario);
      case 2:
        // If we have 2 steps, step 2 is Review. If we have 3 steps, step 2 is Message
        return steps.length === 2 ? 'Review & Confirm' : 'Compose Message';
      case 3:
        return 'Review & Confirm';
      default:
        return '';
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        if (creditData.creditMethod === 'tickets') {
          return creditData.selectedTickets.length > 0;
        } else if (creditData.creditMethod === 'custom') {
          const parsedAmount = parseFloat(creditData.customAmount);
          return !isNaN(parsedAmount) && parsedAmount > 0;
        }
        return false;
      case 2:
        if (!creditData.sendMessage) {
          return true;
        }
        return (
          creditData.messageContent && creditData.messageContent.trim() !== ''
        );
      case 3:
        return true;
      default:
        return false;
    }
  };

  // Dynamic steps based on whether messaging is available
  // Check if transaction has a valid user (either from URL param or transaction data)
  // Use useMemo to recalculate when dependencies change
  const transactionUserId = creditData.transactionInfo?.transactionUserId;
  const steps = useMemo(() => {
    const hasValidUser = customerId || transactionUserId;
    return !hasValidUser || !creditData.sendMessage
      ? ['Select Method', 'Review']
      : ['Select Method', 'Message', 'Review'];
  }, [customerId, transactionUserId, creditData.sendMessage]);

  // Show loading state while fetching data
  if (customerLoading || balanceLoading || transactionLoading) {
    return (
      <EnhancedThemeProvider>
        <PageContainer>
          <PageContent>
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#94A3B8',
              }}
            >
              Loading transaction information...
            </div>
          </PageContent>
        </PageContainer>
      </EnhancedThemeProvider>
    );
  }

  // Show error state if data fetch failed
  if (customerError || balanceError || transactionError) {
    return (
      <EnhancedThemeProvider>
        <PageContainer>
          <PageContent>
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#EF4444',
              }}
            >
              Error loading transaction data:{' '}
              {(customerError || balanceError || transactionError)?.message ||
                'Unknown error'}
            </div>
          </PageContent>
        </PageContainer>
      </EnhancedThemeProvider>
    );
  }

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        <PageContent>
          <StepHeader
            currentStep={currentStep}
            steps={steps}
            backPath={customersPath}
            backLabel="Back"
            onBack={handleBack}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '30px',
            }}
          >
            <PageTitle>{getStepTitle()}</PageTitle>
          </div>
          {getStepComponent()}
          <FooterButtons>
            {currentStep === 1 && (
              <NextButton onClick={handleNext} disabled={!canProceed()}>
                Continue to Message
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </NextButton>
            )}
            {currentStep === 2 && (
              <>
                {/* Show different button based on whether we're on Message or Review */}
                {creditData.sendMessage
                  ? (() => {
                      const scenario =
                        type === 'refund'
                          ? getRefundScenario(
                              creditData.creditRestored,
                              creditData.dynamicRefundAmount,
                              creditData.transactionInfo?.total
                            )
                          : 'normal_refund';
                      return (
                        <NextButton
                          onClick={handleNext}
                          disabled={!canProceed()}
                        >
                          Review{' '}
                          {getAdjustmentText(type, 'button', scenario).replace(
                            'Issue ',
                            ''
                          )}
                          <svg
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M14 5l7 7m0 0l-7 7m7-7H3"
                            />
                          </svg>
                        </NextButton>
                      );
                    })()
                  : (() => {
                      const scenario =
                        type === 'refund'
                          ? getRefundScenario(
                              creditData.creditRestored,
                              creditData.dynamicRefundAmount,
                              creditData.transactionInfo?.total
                            )
                          : 'normal_refund';
                      return (
                        <SendButton
                          onClick={handleIssueCredit}
                          disabled={isProcessing}
                        >
                          {isProcessing ? (
                            <>
                              <SpinnerIcon
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeDasharray="31.4 31.4"
                                  transform="rotate(-90 12 12)"
                                />
                              </SpinnerIcon>
                              Processing...
                            </>
                          ) : (
                            getAdjustmentText(type, 'sendButton', scenario)
                          )}
                        </SendButton>
                      );
                    })()}
              </>
            )}
            {currentStep === 3 &&
              (() => {
                const scenario =
                  type === 'refund'
                    ? getRefundScenario(
                        creditData.creditRestored,
                        creditData.dynamicRefundAmount,
                        creditData.transactionInfo?.total
                      )
                    : 'normal_refund';
                return (
                  <SendButton
                    onClick={handleIssueCredit}
                    disabled={!canProceed() || isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <SpinnerIcon
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm-6 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm6-6.5a.5.5 0 0 1 .5.5V1h1V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
                        </SpinnerIcon>
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                          style={{ marginRight: '8px' }}
                        >
                          <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                        </svg>
                        {getAdjustmentText(type, 'button', scenario)}
                      </>
                    )}
                  </SendButton>
                );
              })()}
          </FooterButtons>
        </PageContent>
      </PageContainer>

      {toasterData && (
        <Toaster
          isOpen={true}
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          onClose={() => setToasterData(null)}
        />
      )}
    </EnhancedThemeProvider>
  );
};

TransactionCredit.propTypes = {
  onSuccess: PropTypes.func,
  type: PropTypes.oneOf(['credit', 'refund']),
};

export default TransactionCredit;
